import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { 
  Phone, 
  PhoneOff, 
  Mic, 
  MicOff, 
  Volume2,
  VolumeX,
  User,
  MessageSquare
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_API_URL}/api`;

const SPEAKR_LOGO_BLACK = "https://customer-assets.emergentagent.com/job_voice-ai-agents-2/artifacts/v0cvlfy5_Modern%20AI%20voice%20intelligence%20logo.png";

export default function VoiceCall() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [isInCall, setIsInCall] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [callDuration, setCallDuration] = useState(0);
  const [currentCallId, setCurrentCallId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const streamRef = useRef(null);
  const callTimerRef = useRef(null);
  const transcriptEndRef = useRef(null);

  useEffect(() => {
    fetchAgents();
    return () => {
      cleanup();
    };
  }, []);

  useEffect(() => {
    if (agentId && agents.length > 0) {
      const agent = agents.find(a => a.id === agentId);
      if (agent) {
        setSelectedAgent(agent);
      }
    }
  }, [agentId, agents]);

  useEffect(() => {
    if (transcriptEndRef.current) {
      transcriptEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [transcript]);

  const fetchAgents = async () => {
    try {
      const res = await axios.get(`${API}/agents`, { headers: getAuthHeader() });
      const agentsData = Array.isArray(res.data) ? res.data : [];
      setAgents(agentsData.filter(a => a.is_active));
      if (!agentId && agentsData.length > 0) {
        const activeAgent = agentsData.find(a => a.is_active);
        if (activeAgent) setSelectedAgent(activeAgent);
      }
    } catch (error) {
      toast.error("Failed to load agents");
    }
  };

  const cleanup = () => {
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
  };

  const startCall = async () => {
    if (!selectedAgent) {
      toast.error("Please select an agent first");
      return;
    }

    try {
      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Create audio context
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();

      // Start call in backend
      const formData = new FormData();
      formData.append('agent_id', selectedAgent.id);
      const callRes = await axios.post(`${API}/calls`, formData, { headers: getAuthHeader() });
      setCurrentCallId(callRes.data.id);
      setSessionId(`call-${callRes.data.id}-${Date.now()}`);

      setIsInCall(true);
      setCallDuration(0);
      setTranscript([]);

      // Start timer
      callTimerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);

      // Add greeting
      const greeting = `Hello! I'm ${selectedAgent.name}. How can I help you today?`;
      setTranscript([{ role: "assistant", content: greeting }]);
      
      // Play greeting
      await playAgentResponse(greeting);

      toast.success("Call started");
    } catch (error) {
      console.error("Error starting call:", error);
      if (error.name === 'NotAllowedError') {
        toast.error("Microphone permission denied. Please allow microphone access.");
      } else {
        toast.error("Failed to start call");
      }
    }
  };

  const endCall = async () => {
    cleanup();

    if (currentCallId) {
      try {
        const formData = new FormData();
        formData.append('duration_seconds', callDuration);
        await axios.put(`${API}/calls/${currentCallId}/end`, formData, { headers: getAuthHeader() });

        // Save transcript
        for (const msg of transcript) {
          const transcriptForm = new FormData();
          transcriptForm.append('role', msg.role);
          transcriptForm.append('content', msg.content);
          await axios.put(`${API}/calls/${currentCallId}/transcript`, transcriptForm, { headers: getAuthHeader() });
        }
      } catch (error) {
        console.error("Error ending call:", error);
      }
    }

    setIsInCall(false);
    setCurrentCallId(null);
    setSessionId(null);
    toast.info("Call ended");
  };

  const toggleMute = () => {
    if (streamRef.current) {
      streamRef.current.getAudioTracks().forEach(track => {
        track.enabled = isMuted;
      });
    }
    setIsMuted(!isMuted);
  };

  const toggleSpeaker = () => {
    setIsSpeakerOn(!isSpeakerOn);
  };

  const startListening = async () => {
    if (!streamRef.current || isMuted || isProcessing) return;

    try {
      setIsListening(true);
      audioChunksRef.current = [];

      const mediaRecorder = new MediaRecorder(streamRef.current, {
        mimeType: 'audio/webm'
      });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        if (audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          await processAudio(audioBlob);
        }
      };

      mediaRecorder.start();

      // Auto-stop after 10 seconds
      setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          stopListening();
        }
      }, 10000);
    } catch (error) {
      console.error("Error starting recording:", error);
      setIsListening(false);
    }
  };

  const stopListening = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsListening(false);
  };

  const processAudio = async (audioBlob) => {
    setIsProcessing(true);

    try {
      // Convert to text using STT
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      
      const sttRes = await axios.post(`${API}/voice/stt`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const userText = sttRes.data.text;
      if (!userText || userText.trim() === '') {
        toast.info("Couldn't hear you. Try again.");
        setIsProcessing(false);
        return;
      }

      // Add user message to transcript
      setTranscript(prev => [...prev, { role: "user", content: userText }]);

      // Get AI response
      const chatRes = await axios.post(`${API}/voice/chat`, {
        agent_id: selectedAgent.id,
        message: userText,
        session_id: sessionId
      }, { headers: getAuthHeader() });

      // Add assistant response to transcript
      setTranscript(prev => [...prev, { role: "assistant", content: chatRes.data.response }]);

      // Play audio response
      if (chatRes.data.audio_base64 && isSpeakerOn) {
        await playAudioBase64(chatRes.data.audio_base64);
      }
    } catch (error) {
      console.error("Error processing audio:", error);
      toast.error("Failed to process your message");
    } finally {
      setIsProcessing(false);
    }
  };

  const playAgentResponse = async (text) => {
    if (!isSpeakerOn) return;
    
    try {
      const formData = new FormData();
      formData.append('text', text);
      formData.append('voice', selectedAgent?.voice || "female_professional");
      
      const res = await axios.post(`${API}/voice/tts`, formData, { 
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.audio_base64) {
        await playAudioBase64(res.data.audio_base64);
      } else {
        // Fallback to browser TTS
        speakWithBrowserTTS(text);
      }
    } catch (error) {
      console.error("Error playing response:", error);
      speakWithBrowserTTS(text);
    }
  };

  const speakWithBrowserTTS = (text) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1;
      utterance.pitch = 1;
      window.speechSynthesis.speak(utterance);
    }
  };

  const playAudioBase64 = async (base64Audio) => {
    try {
      const audioData = atob(base64Audio);
      const arrayBuffer = new ArrayBuffer(audioData.length);
      const view = new Uint8Array(arrayBuffer);
      for (let i = 0; i < audioData.length; i++) {
        view[i] = audioData.charCodeAt(i);
      }

      const audioContext = audioContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);
      source.start(0);
    } catch (error) {
      console.error("Error playing audio:", error);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="h-full flex flex-col" data-testid="voice-call-page">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={SPEAKR_LOGO_BLACK} alt="Speakr" className="h-10 w-auto" />
            <div className="h-8 w-px bg-border" />
            <div>
              <h1 className="text-xl font-heading font-bold">Voice Call</h1>
              <p className="text-sm text-muted-foreground">
                {isInCall ? `In call with ${selectedAgent?.name}` : "Select an agent to start"}
              </p>
            </div>
          </div>
          {!isInCall && (
            <Select 
              value={selectedAgent?.id || ""} 
              onValueChange={(val) => {
                const agent = agents.find(a => a.id === val);
                setSelectedAgent(agent);
                navigate(`/call/${val}`);
              }}
            >
              <SelectTrigger className="w-[200px]" data-testid="agent-select">
                <SelectValue placeholder="Select agent" />
              </SelectTrigger>
              <SelectContent>
                {agents.map((agent) => (
                  <SelectItem key={agent.id} value={agent.id} data-testid={`agent-option-${agent.name.toLowerCase()}`}>
                    {agent.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Main Call Interface */}
      <div className="flex-1 flex">
        {/* Call Area */}
        <div className="flex-1 flex flex-col items-center justify-center p-8 call-bg relative">
          {/* Background Glow */}
          {isInCall && (
            <div className="hero-glow absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          )}

          {/* Agent Avatar */}
          <div className="relative mb-8">
            <div className={cn(
              "relative z-10",
              isInCall && "avatar-ring"
            )}>
              <Avatar className="w-32 h-32 border-4 border-card">
                <AvatarImage src={selectedAgent?.avatar_url} alt={selectedAgent?.name} />
                <AvatarFallback className="text-4xl">
                  {selectedAgent?.name?.[0] || <User className="w-12 h-12" />}
                </AvatarFallback>
              </Avatar>
            </div>
            
            {/* Pulse rings when in call */}
            {isInCall && (
              <>
                <div className="absolute inset-0 rounded-full bg-primary/20 pulse-ring" />
                <div className="absolute inset-0 rounded-full bg-primary/10 pulse-ring" style={{ animationDelay: '0.5s' }} />
              </>
            )}
          </div>

          {/* Agent Info */}
          <h2 className="text-2xl font-heading font-bold mb-2">
            {selectedAgent?.name || "Select an Agent"}
          </h2>
          <p className="text-muted-foreground mb-2">
            {selectedAgent?.personality || "No agent selected"}
          </p>

          {/* Call Duration */}
          {isInCall && (
            <p className="text-3xl font-mono text-primary mb-8" data-testid="call-duration">
              {formatDuration(callDuration)}
            </p>
          )}

          {/* Voice Indicator */}
          {isInCall && isListening && (
            <div className="flex items-end gap-1 h-8 mb-8" data-testid="voice-indicator">
              {[...Array(5)].map((_, i) => (
                <div 
                  key={i}
                  className="w-1 bg-primary rounded-full voice-wave-bar"
                  style={{ height: '100%' }}
                />
              ))}
            </div>
          )}

          {/* Processing Indicator */}
          {isProcessing && (
            <div className="flex items-center gap-2 text-primary mb-8" data-testid="processing-indicator">
              <div className="loading-dots flex gap-1">
                <span className="w-2 h-2 bg-primary rounded-full"></span>
                <span className="w-2 h-2 bg-primary rounded-full"></span>
                <span className="w-2 h-2 bg-primary rounded-full"></span>
              </div>
              <span>Processing...</span>
            </div>
          )}

          {/* Call Controls */}
          <div className="flex items-center gap-4">
            {!isInCall ? (
              <Button
                size="lg"
                className="rounded-full px-12 py-6 text-lg glow-primary"
                onClick={startCall}
                disabled={!selectedAgent}
                data-testid="start-call-btn"
              >
                <Phone className="w-6 h-6 mr-2" />
                Start Call
              </Button>
            ) : (
              <>
                {/* Mute Button */}
                <Button
                  size="lg"
                  variant={isMuted ? "destructive" : "secondary"}
                  className="rounded-full w-14 h-14"
                  onClick={toggleMute}
                  data-testid="mute-btn"
                >
                  {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
                </Button>

                {/* Push to Talk Button */}
                <Button
                  size="lg"
                  className={cn(
                    "rounded-full w-20 h-20 transition-all",
                    isListening ? "bg-primary scale-110 glow-primary" : "bg-secondary"
                  )}
                  onMouseDown={startListening}
                  onMouseUp={stopListening}
                  onMouseLeave={stopListening}
                  onTouchStart={startListening}
                  onTouchEnd={stopListening}
                  disabled={isMuted || isProcessing}
                  data-testid="push-to-talk-btn"
                >
                  <Mic className={cn("w-8 h-8", isListening && "animate-pulse")} />
                </Button>

                {/* Speaker Button */}
                <Button
                  size="lg"
                  variant={isSpeakerOn ? "secondary" : "destructive"}
                  className="rounded-full w-14 h-14"
                  onClick={toggleSpeaker}
                  data-testid="speaker-btn"
                >
                  {isSpeakerOn ? <Volume2 className="w-6 h-6" /> : <VolumeX className="w-6 h-6" />}
                </Button>

                {/* End Call Button */}
                <Button
                  size="lg"
                  variant="destructive"
                  className="rounded-full w-14 h-14 glow-destructive"
                  onClick={endCall}
                  data-testid="end-call-btn"
                >
                  <PhoneOff className="w-6 h-6" />
                </Button>
              </>
            )}
          </div>

          {/* Instructions */}
          {isInCall && !isProcessing && (
            <p className="text-sm text-muted-foreground mt-6">
              Hold the microphone button to speak
            </p>
          )}
        </div>

        {/* Transcript Panel */}
        {isInCall && (
          <Card className="w-96 border-l border-border rounded-none bg-card/50" data-testid="transcript-panel">
            <div className="p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-primary" />
                <h3 className="font-heading font-semibold">Live Transcript</h3>
              </div>
            </div>
            <ScrollArea className="h-[calc(100vh-200px)]">
              <div className="p-4 space-y-4">
                {transcript.map((msg, index) => (
                  <div 
                    key={index}
                    className={cn(
                      "transcript-message p-3 rounded-lg",
                      msg.role === "user" 
                        ? "bg-primary/10 ml-8" 
                        : "bg-secondary/50 mr-8"
                    )}
                    data-testid={`transcript-message-${index}`}
                  >
                    <p className="text-xs text-muted-foreground mb-1 font-medium">
                      {msg.role === "user" ? "You" : selectedAgent?.name}
                    </p>
                    <p className="text-sm">{msg.content}</p>
                  </div>
                ))}
                <div ref={transcriptEndRef} />
              </div>
            </ScrollArea>
          </Card>
        )}
      </div>
    </div>
  );
}
